<template>
  <v-card class="back" style="height: 480px">
    <v-layout fill-height>
      <v-flex xs6>
        <v-layout column fill-height>
          <scroll-area>
            <template slot="header">
              <v-toolbar dark color="secondary" dense flat>
                <v-toolbar-title>
                  <span class="body-2 text-uppercase">{{$t('add foreign measurement')}}</span>
                </v-toolbar-title>
              </v-toolbar>
            </template>

            <template slot="body">
              <v-layout column fill-height justify-start>

                <v-treeview
                  disable-resize-watcher="true"
                  style="cursor: pointer"
                  :items="getTree"
                  :active.sync="treeActive"
                  :open.sync="treeOpened"
                  :load-children="loadCh"
                  loading-icon="highlight_off"
                  active-class="secondary lighten-5"
                  class="caption font-weight-medium font-condensend"
                  item-disabled="disabled"
                  activatable
                  item-key="$id"
                  return-object
                >

                  <template v-slot:label="{ item, open, active }" :data-testid="$options.name + '__template__' + item.$id">

                    <v-tooltip color="back" top :nudge-bottom="10" :nudge-left="1500" close-delay="0">
                        <v-layout slot="activator" @contextmenu.prevent="contextHandler" fill-height align-center class="treeLeaf" px-0>
                          <svg-icon
                            :color="$getColor($store.state.monitoring.status[item.$id])"
                            name="circle"
                            width="14"
                          />
                          <div style="width: 10px"></div>
                          <svg-icon
                            color="blackLine"
                            :name="$checkItem(item)"
                            width="28"
                          />
                          <span class="body-1 font-weight-medium font-condensend px-2">{{
                            item.name
                          }}</span>
                        </v-layout>
                      <span class="blacktext--text">{{$t(item.name)}}</span>
                    </v-tooltip>

                  </template>
                </v-treeview>
              </v-layout>
            </template>
          </scroll-area>
        </v-layout>
      </v-flex>

      <v-flex xs6>
        <v-layout column fill-height>
          <scroll-area>
            <template slot="header">
              <v-toolbar dark color="secondary" dense flat>
                <v-toolbar-title>
                  <span class="body-2 text-uppercase">{{$t('measurements')}}</span>
                </v-toolbar-title>
              </v-toolbar>
            </template>

            <template slot="body">
              <v-layout column fill-height justify-start>
                <loading-circle
                  :show="loader"
                />
                <v-list
                  v-if="getMeasures"
                  v-for="(item, index) in getMeasures"
                  :key="index"
                  dense
                >
                  <v-list-tile>
                    <v-list-tile-content>
                      <v-list-tile-title class="text--primary">{{ item.name }}</v-list-tile-title>
                    </v-list-tile-content>

                    <v-list-tile-action>
                      <v-tooltip dark color="secondary" left>
                        <v-btn icon slot="activator" small dark color="secondary" 
                        :data-testid="$options.name + '__btn__' + 'add' + index"
                        @click="() => clickHandler(item)">
                          <v-icon color="yellow darken-2">
                            add
                          </v-icon>
                        </v-btn>
                        <span>{{$t('plug measurement')}}</span>
                      </v-tooltip>
                    </v-list-tile-action>
                  </v-list-tile>
                </v-list>
              </v-layout>
            </template>

            <template slot="footer">
              <v-layout fill-height>
                <v-flex xs2>
                </v-flex>
                <v-layout fill-height justify-end>
                  <v-btn dark small 
                  :data-testid="$options.name + '__btn__' + 'close'"
                  @click="()=>{$emit('close')}" color="grey">{{$t('close')}}</v-btn>
                </v-layout>
              </v-layout>
            </template>
          </scroll-area>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'
import loadingCircle from '@/nms/components/partials/loader.vue'
import scrollArea from '@/nms/components/partials/scrollarea.vue'
import svgIcon from '@/nms/components/media/svg/static/svg-icon.vue'

// Типы
interface TreeNode {
  $id?: string
  id?: string
  name?: string
  cls?: string
  type?: string
  children?: TreeNode[]
  parents?: any[]
  [key: string]: any
}

interface MeasureItem {
  $id?: string
  id?: string
  name?: string
  [key: string]: any
}

interface TreeState {
  tree: TreeNode[]
  treeActive: TreeNode[]
  treeOpened: TreeNode[]
}

export default Vue.extend({
  name: 'ADD_FOREIGN_CARD',

  components: {
    loadingCircle,
    scrollArea,
    svgIcon
  },

  props: {
    data: {
      type: Array as () => any[],
      default: () => []
    },
  },

  data(): TreeState & {
    loader: boolean
    measures: MeasureItem[] | null
    mirror: any | null
  } {
    return {
      loader: false,
      measures: null,
      mirror: null,
      tree: [],
      treeActive: [],
      treeOpened: []
    }
  },

  computed: {
    getData(): any[] {
      return this.data
    },

    getMeasures(): MeasureItem[] | null {
      return this.measures
    },

    getTree(): TreeNode[] {
      return this.tree
    },
  },

  watch: {
    treeActive: {
      handler(newVal: TreeNode[]): void {
        console.log('WATCH', this.$options.name, 'treeActive', newVal)
        if (newVal.length) {
          this.loadMeasures(newVal[0])
        }
      },
      deep: true
    },
  },

  created(): void {
    this.loadTree()
  },

  methods: {
    async loadTree(): Promise<void> {
      try {
        const query = {
          id: ['/physical'],
          notNull: true,
          data: {
            namePath: true,
            children: {
              data: { children: true },
              filter: { or: [{ cls: 'location' }, { cls: 'cabinet' }, { cls: 'device' }] }
            },
          }
        }
        const co = await this.$bridge.cmdbQuery(query) as any[]

        if (co) {
          console.log(this.$options.name, 'loadTree', 'co$', co)
          const cycle = (arr: TreeNode[]): TreeNode[] => {
            arr.forEach(item => {
              if (item.children?.length) {
                item.children = []
              }
            })
            return arr.filter(it => !(it.cls === 'location' && !it.type))
          }
          const children = cycle(co[0].children)
          this.tree = children
          this.createTreeHeap()
        } else {
          alert('smth wrong with server')
          console.log(this.$options.name, 'loadTree', 'smth wrong')
        }
      } catch (err) {
        console.error('CATCH', this.$options.name, 'LOADTREE', err)
      }
    },

    loadCh(item: TreeNode): void {
      this.$nextTick(() => {
        this.loadChildren(item)
      })
    },

    async loadChildren(data: TreeNode): Promise<void> {
      try {
        // this.setLoader(this.$options.name, 'loadChildren', true)

        let parents
        if (data.parents) {
          parents = data.parents
        } else {
          parents = await data.getParents()
          parents.splice(0, 1)
        }

        console.log(this.$options.name, 'loadChildren', 'data', data)
        console.log(this.$options.name, 'loadChildren', 'TREE_OPEN', this.treeOpened)

        const query = {
          id: [data.$id],
          notNull: true,
          data: {
            namePath: true,
            children: {
              data: { children: true },
              filter: {
                or: [
                  { cls: 'location' },
                  { cls: 'cabinet' },
                  { cls: 'device' },
                  { cls: 'module' },
                  { cls: 'slot' },
                  { cls: 'port' }
                ]
              }
            },
          }
        }
        const co = await this.$bridge.cmdbQuery(query) as any[]

        if (co) {
          console.log(this.$options.name, 'loadChildren', 'co$', co)
          const cycle = (arr: TreeNode[]): TreeNode[] => {
            arr.forEach(item => {
              if (item.children?.length === 0) {
                delete item.children
              }
              if (item.children?.length) {
                item.children = []
              }
            })
            return arr.filter(it => !(it.cls === 'location' && !it.type))
          }
          const children = cycle(co[0].children)

          const updateTree = (arr: TreeNode[]): void => {
            arr.forEach((item, i) => {
              if (item.$id === data.$id) {
                if (children.length) {
                  data.children = children
                }
                this.$set(arr, i, data)

                if (this.treeActive.length && this.treeActive[0]?.$id === data.$id) {
                  this.$set(this.treeActive, 0, data)
                }
                if (this.treeOpened.length && this.treeOpened[this.treeOpened.length - 1]?.$id === data.$id) {
                  this.$set(this.treeOpened, this.treeOpened.length - 1, data)
                }
                return
              }
              if (item.children) {
                updateTree(item.children)
              }
            })
          }

          // если были открыты далёкие потомки, то их тоже подгружаем
          this.treeOpened.forEach(item => {
            const f = children.find(child => child.$id === item.$id)
            if (f) {
              console.log(this.$options.name, 'loadChildren', 'FIND!!!!', f)
              f.parents = parents
              this.loadChildren(f)
            }
          })

          updateTree(this.tree)

          if (this.treeOpened.length !== parents.length) {
            console.log(this.$options.name, 'treeOpened', this.treeOpened)
            console.log(this.$options.name, 'parents', parents)
            this.treeOpened = [...this.treeOpened, ...parents]
          }

          this.createTreeHeap()
          // this.setLoader(this.$options.name, 'loadChildren', false)
        } else {
          console.log(this.$options.name, 'loadChildren', 'getLinks', 'SMTH WRONG')
          // this.setLoader(this.$options.name, 'loadChildren', false)
        }
      } catch (err) {
        console.error('CATCH', this.$options.name, 'LOAD CHILDREN', err)
        // this.setLoader(this.$options.name, 'loadChildren', false)
      }
    },

    async loadMeasures(obj: TreeNode): Promise<void> {
      try {
        // this.setLoader(this.$options.name, 'loadMeasures', true)
        // this.loader = true

        const device$ = await this.$bridge.getObjectById(obj.$id)
        console.log(this.$options.name, 'loadMeasures', 'device$', device$)

        if (device$.$id) {
          this.measures = await device$.getMeasures(true, false)
          console.log(this.$options.name, 'loadMeasures', 'measures', this.measures)
        }

        // this.loader = false
        // this.setLoader(this.$options.name, 'loadMeasures', false)
      } catch (err) {
        console.error('CATCH', this.$options.name, 'LOAD CHILDREN', err)
        // this.setLoader(this.$options.name, 'loadMeasures', false)
      }
    },

    async createTreeHeap(): Promise<void> {
      this.statusUnsubscribe(this.$options.name || 'ADD_FOREIGN_CARD')

      const heap: TreeNode[] = []
      const cycle = (arr: TreeNode[]): void => {
        arr.forEach(item => {
          if (item.$id) {
            heap.push(item)
            this.$store.dispatch('addStatusKey', item)
          }
          if (item.children) {
            cycle(item.children)
          }
        })
      }
      cycle(this.tree)

      this.statusSubscribe(heap, this.$options.name || 'ADD_FOREIGN_CARD')
    },

    async statusSubscribe(arr: TreeNode[], group: string): Promise<void> {
      console.log(this.$options.name, 'statusSubscribe', 'arr', arr)
      console.log(this.$options.name, 'statusSubscribe', 'getEditMode', this.$store.getters.getEditMode)

      this.$store.dispatch('measuresSubscribeInit', {
        name: 'addStatus',
        group: group,
        listener: 'status'
      })

      if (arr.length) {
        await this.$bridge.subscribe(arr.map(obj => obj.$id))
      }
    },

    subscribeHandler(e: { $id?: string; [key: string]: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    clickHandler(val: MeasureItem): void {
      console.log(this.$options.name, 'clickHandler', 'val', val)
      this.$emit('plugMeasurement', val)
    },

    setLoader(name: string, status: boolean): void {
      this.$store.dispatch('setLoader', {
        key: name,
        value: status
      })
    },

    contextHandler(e: Event): void {
      console.log('contextHandler', e)
    },
  },
})
</script>

<style lang="css" scoped>
</style>
